import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getLoginRequest } from '../auth/msal-config.js';
import { setupHash } from '../api/athlete-sign-in.js';
import { CompleteCoachingProfile, type InvitationDetails } from './complete-coaching-profile.js';
import { WaitNote } from './wait-note.js';

/**
 * The invitation's link, opened by somebody not signed in (stage C; canvas row
 * 11, "Parent · the link: which email, three steps", approved 16 October).
 *
 * Names the address the coach typed and says the three steps, in Matthew's
 * approved words (asked for after the walk: which email to use, and good 1 2 3
 * instructions before the button).
 *
 * Sign up opens Microsoft's Create account (prompt=create) with the address
 * in the box (login_hint): seen in a fresh browser on 17 October. A browser
 * already signed in to this directory shows Microsoft's account list first;
 * a new parent's has none. The code is Microsoft's, and proves the address.
 *
 * A family already claimed is asked to sign in with the address, no steps,
 * and gets Microsoft's account list when signed in (ruled 17 October): a
 * wrong account is caught here, "This link is for", and attaches nothing.
 * Back from Microsoft, the same link claims the family (welcome-route.tsx).
 *
 * A second parent's link (/join, canvas row D) is the same page, read from
 * their invitation: "the stage C page, as built".
 *
 * SINCE 21 OCTOBER a family's link not yet used opens "Complete your coaching
 * profile" instead (complete-coaching-profile.tsx; mockup *Clients and Texted
 * Links* v8), for texted and emailed links alike. What is left here is the
 * claimed family's Sign in, and the second parent's page, unchanged.
 *
 * A USED LINK (ruled 24 October, canvas *Sign-up Link Fixes* board 3) says so
 * in Matthew's words and offers Sign in. It no longer shows the address it was
 * used with - anyone holding the link would see it - nor fills it in at
 * Microsoft. Signing up again with it was already impossible.
 */

type Look =
  | { status: 'looking' }
  | { status: 'dead' }
  | { status: 'ready'; email: string; claimed: boolean }
  | { status: 'complete'; hash: string; details: InvitationDetails };

export function WelcomeSignUp() {
  const { instance } = useMsal();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const lookup = pathname === '/join' ? '/api/second-parent-invitations' : '/api/family-invitations';
  const token = params.get('token') ?? '';
  const [look, setLook] = useState<Look>({ status: 'looking' });

  useEffect(() => {
    let cancelled = false;
    if (!/^[A-Za-z0-9_-]{20,}$/.test(token)) {
      setLook({ status: 'dead' });
      return;
    }
    void (async () => {
      try {
        const hash = await setupHash(token);
        const response = await fetch(`${lookup}/${hash}`, {
          headers: { 'x-portal-client-id': String(import.meta.env['VITE_ENTRA_CLIENT_ID'] ?? '') },
        });
        if (cancelled) return;
        if (!response.ok) {
          setLook({ status: 'dead' });
          return;
        }
        const found = (await response.json()) as { email: string; claimed: boolean } & Partial<InvitationDetails>;
        if (pathname !== '/join' && !found.claimed && found.athletes) {
          setLook({ status: 'complete', hash, details: found as InvitationDetails });
          return;
        }
        setLook({ status: 'ready', ...found });
      } catch {
        if (!cancelled) setLook({ status: 'dead' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, lookup, pathname]);

  if (look.status === 'looking') return <WaitNote>Checking…</WaitNote>;
  if (look.status === 'complete') return <CompleteCoachingProfile hash={look.hash} details={look.details} />;

  if (look.status === 'dead') {
    return (
      <section className="panel">
        <h2>This link no longer works</h2>
      </section>
    );
  }

  const { email, claimed } = look;

  if (claimed) {
    return (
      <section className="panel welcome">
        <h2>This link has already been used.</h2>
        <p>Already signed up with this link? Sign in below.</p>
        <button type="button" className="primary" onClick={() => void instance.loginRedirect(getLoginRequest())}>
          Sign in
        </button>
      </section>
    );
  }

  const go = () =>
    void instance.loginRedirect({
      ...getLoginRequest(),
      loginHint: email,
      prompt: 'create',
    });

  return (
    <section className="panel welcome">
      <h2>{`Sign up with ${email}`}</h2>
      <ol className="welcome-steps">
        <li>
          Tap <b>Sign up</b>. Your email is filled in. Tap <b>Next</b>.
        </li>
        <li>
          Microsoft emails a code to <b>{email}</b>. Enter it.
        </li>
        <li>Choose a password.</li>
      </ol>
      <button type="button" className="primary" onClick={go}>
        Sign up
      </button>
    </section>
  );
}
